import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import FeedbackForm from '@/pages/about/components/FeedbackForm';
import { categories } from '@/mocks/categories';
import { usePageMeta } from '@/hooks/usePageMeta';

const SERVICES = [
  {
    icon: 'ri-film-line',
    title: '丰富内容聚合',
    desc: '汇聚电影、电视剧、综艺与短视频四大类内容，覆盖多种题材与风格，满足不同观看偏好。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '精准分类筛选',
    desc: '按年份、地区与热度排序快速筛选，帮助你在海量内容中迅速定位想看的那一部。',
  },
  {
    icon: 'ri-search-line',
    title: '便捷搜索推荐',
    desc: '支持标题关键词搜索与个性化推荐，让发现好内容变得简单高效。',
  },
  {
    icon: 'ri-heart-line',
    title: '收藏与分享',
    desc: '收藏喜欢的内容保存在本地随时查看，一键复制链接把好片分享给朋友。',
  },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 在线视频',
    description:
      '在线视频是海量视频资源在线观看平台，汇聚电影、电视剧、综艺与短视频，提供分类筛选、搜索与收藏功能，致力于提升用户的发现与观影体验。',
    keywords: '关于在线视频,视频平台介绍,在线观看平台,反馈',
  });

  return (
    <SiteLayout>
      <section className="relative w-full h-56 md:h-72 overflow-hidden bg-secondary-950">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20video%20streaming%20platform%20team%20workspace%2C%20people%20watching%20screens%20in%20a%20bright%20studio%20surrounded%20by%20floating%20media%20posters%2C%20cool%20blue%20and%20warm%20accent%20lighting%2C%20clean%20creative%20tech%20atmosphere&width=1600&height=700&seq=about-hero&orientation=landscape"
          alt="在线视频平台"
          title="在线视频 - 海量视频资源在线观看平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/90 via-secondary-950/70 to-secondary-950/40"></div>
        <div className="relative z-10 h-full w-full px-4 md:px-6 flex flex-col justify-center">
          <nav className="flex items-center gap-1.5 text-sm text-background-300 mb-4" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-background-50">
            关于在线视频
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-background-200 leading-relaxed">
            一个专注于视频内容发现与观看体验的平台，让每一次打开都有好内容值得期待。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">
              网站定位
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              在线视频是一家海量视频资源在线观看平台，面向喜爱影视剧、综艺节目及短视频的广大用户群体。
              我们致力于提供丰富视频内容的浏览与在线播放服务，满足用户在多类别、多类型视频中的搜索、筛选和观看需求。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              我们希望用清晰的信息层级和流畅的浏览体验，帮助每一位用户快速发现感兴趣的视频，
              把“找片”这件事变得轻松愉快。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="rounded-lg bg-background-100 border border-background-200 px-4 py-3 text-center">
                <p className="font-display text-2xl font-bold text-primary-700">4</p>
                <p className="mt-1 text-xs text-foreground-500">内容分类</p>
              </div>
              <div className="rounded-lg bg-background-100 border border-background-200 px-4 py-3 text-center">
                <p className="font-display text-2xl font-bold text-primary-700">36+</p>
                <p className="mt-1 text-xs text-foreground-500">精选视频</p>
              </div>
              <div className="rounded-lg bg-background-100 border border-background-200 px-4 py-3 text-center">
                <p className="font-display text-2xl font-bold text-primary-700">7×24</p>
                <p className="mt-1 text-xs text-foreground-500">随时观看</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden border border-background-200 h-64 md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20home%20movie%20night%20scene%2C%20large%20smart%20TV%20showing%20a%20colorful%20film%2C%20comfortable%20sofa%20with%20popcorn%20and%20soft%20warm%20lamp%20glow%2C%20inviting%20modern%20living%20room%2C%20cinematic%20warm%20ambiance&width=1000&height=800&seq=about-intro&orientation=squarish"
              alt="在家观看视频"
              title="在线视频 - 便捷的在家观影体验"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">
            服务范围与内容介绍
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
            围绕“发现好内容”这一核心，我们为不同观看习惯的用户提供全方位的浏览与体验支持。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {SERVICES.map((s) => (
            <div
              key={s.title}
              className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6"
            >
              <span className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className={`${s.icon} text-primary-700 text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-display text-base font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 border border-background-300 hover:border-primary-400 text-sm text-foreground-800 transition-colors cursor-pointer"
            >
              <i className={`${c.icon} text-primary-600`}></i>
              {c.name}频道
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              你的每一条反馈，都是我们进步的动力。无论是内容建议、功能意见还是使用中遇到的问题，
              都欢迎通过下方表单告诉我们。
            </p>
            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-primary-700 text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">联系邮箱</p>
                  <p className="text-sm text-foreground-500">support@online-video.example.com</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-primary-700 text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">服务时间</p>
                  <p className="text-sm text-foreground-500">每日 9:00 - 22:00</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-chat-smile-2-line text-primary-700 text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">反馈处理</p>
                  <p className="text-sm text-foreground-500">一般 1-2 个工作日内回复</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <FeedbackForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}